import React from "react";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
  AccountBalanceRounded,
  EmojiEventsRounded,
  ReceiptLongRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../common/SearchBar/SearchBar";
import { useMembership } from "../../contexts/MembershipContext";
import { hapticSelection } from "../../lib/haptics";
import { isNative } from "../../lib/platform";
import { MOBILE_TOPBAR_HEIGHT } from "../../contexts/SidebarContext";
import { SERIF_DISPLAY } from "../Chat/ChatHero";
import { heroRise } from "../Chat/chatMotion";
import {
  NativeIconChip,
  NativeLandingContainer,
  NativeLandingHeader,
  NativeListRow,
  NativeSectionLabel,
} from "../Native/NativeLanding";

const destinations = [
  {
    tab: "super-investors",
    title: "Super Investors",
    description: "Explore leading investors’ holdings and portfolio moves.",
    icon: EmojiEventsRounded,
    membersOnly: false,
  },
  {
    tab: "congressional",
    title: "Congressional Portfolios",
    description: "See the stocks members of Congress are buying and selling.",
    icon: AccountBalanceRounded,
    membersOnly: true,
  },
  {
    tab: "trades",
    title: "Latest Trade Disclosures",
    description: "Follow the latest trades disclosed by the House and Senate.",
    icon: ReceiptLongRounded,
    membersOnly: true,
  },
];
const InsiderTradesLandingPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { isMembershipActive } = useMembership();
  const search = (
    <Box data-tour="congressional-search" sx={{ width: "100%" }}>
      <Tooltip
        title={!isMembershipActive ? "Available with Insight Pro" : ""}
        arrow
      >
        <span style={{ display: "block" }}>
          <SearchBar
            placeholder="Search any stock for congressional activity..."
            width="100%"
            variant="chatgpt"
            disabled={!isMembershipActive}
            onSelectItem={(item) => {
              hapticSelection();
              navigate(
                `/super-investors?ticker=${encodeURIComponent(item.symbol)}`,
              );
            }}
          />
        </span>
      </Tooltip>
    </Box>
  );
  const openSection = (tab: string) => {
    hapticSelection();
    navigate("/super-investors", { state: { investorSection: tab } });
  };
  if (isNative())
    return (
      <NativeLandingContainer>
        <NativeLandingHeader
          title="Insider Trades"
          subtitle="Find out which stocks Congress is buying and selling"
        />
        {search}
        <NativeSectionLabel>Explore</NativeSectionLabel>
        <Stack spacing={1.25} data-tour="insider-trades-options">
          {destinations.map(({ icon: Icon, ...destination }) => (
            <NativeListRow
              key={destination.tab}
              leading={
                <NativeIconChip color={theme.palette.primary.main}>
                  <Icon sx={{ fontSize: 22 }} />
                </NativeIconChip>
              }
              title={destination.title}
              subtitle={destination.description}
              onClick={() => openSection(destination.tab)}
              disabled={destination.membersOnly && !isMembershipActive}
            />
          ))}
        </Stack>
      </NativeLandingContainer>
    );
  return (
    <Box
      sx={{
        height: {
          xs: `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px - env(safe-area-inset-top, 0px))`,
          lg: "100vh",
        },
        overflowY: "auto",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          minHeight: "100%",
          display: { xs: "flex", lg: "grid" },
          flexDirection: "column",
          justifyContent: "center",
          gridTemplateRows: "1fr auto 1fr",
          width: "100%",
          maxWidth: 680,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 6 },
          pb: {
            xs: "calc(32px + env(safe-area-inset-bottom, 0px))",
            md: "calc(48px + env(safe-area-inset-bottom, 0px))",
            lg: 6,
          },
        }}
      >
        <Box sx={{ display: { xs: "none", lg: "block" } }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          <Box sx={{ mb: 3, ...heroRise(0) }}>
            <img
              src={
                isDark
                  ? "/insightLongLogoPrimaryWhiteText.svg"
                  : "/insightLongLogoPrimary.svg"
              }
              alt="Insight Analytics"
              style={{ height: 48, width: "auto", maxWidth: "100%" }}
            />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: SERIF_DISPLAY,
              fontWeight: 500,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
              color: "text.primary",
              mb: { xs: 3.5, md: 4.5 },
              ...heroRise(0.05),
            }}
          >
            What is Congress trading?
          </Typography>
          <Box sx={{ width: "100%", ...heroRise(0.14) }}>{search}</Box>
        </Box>
        <Box
          data-tour="insider-trades-options"
          sx={{ alignSelf: "start", width: "100%", pt: { xs: 5, md: 7 } }}
        >
          <Typography
            sx={{
              fontSize: "0.62rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.09em",
              color: "text.disabled",
              mb: 1,
              ...heroRise(0.14),
            }}
          >
            Explore
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1,
            }}
          >
            {destinations.map(({ icon: Icon, ...destination }, index) => {
              const disabled = destination.membersOnly && !isMembershipActive;
              return (
                <Tooltip
                  key={destination.tab}
                  title={
                    disabled
                      ? "Available with Insight Pro"
                      : destination.description
                  }
                  arrow
                >
                  <span style={{ display: "flex" }}>
                    <Box
                      component="button"
                      type="button"
                      disabled={disabled}
                      onClick={() => openSection(destination.tab)}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        width: "100%",
                        height: "100%",
                        minHeight: { xs: 88, sm: 136 },
                        textAlign: "left",
                        px: 2,
                        py: 2,
                        cursor: "pointer",
                        opacity: disabled ? 0.5 : 1,
                        borderRadius: 1.5,
                        fontFamily: "inherit",
                        fontSize: "0.9375rem",
                        fontWeight: 500,
                        lineHeight: 1.4,
                        color: "text.secondary",
                        border: 1,
                        borderColor: "divider",
                        bgcolor: "transparent",
                        transition:
                          "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
                        "&:hover": {
                          bgcolor: "action.hover",
                          borderColor: "primary.main",
                          color: "text.primary",
                        },
                        "&:hover svg": { color: "primary.main" },
                        "&:focus-visible": {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                        ...heroRise(0.18 + index * 0.05),
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 22,
                          flexShrink: 0,
                          mt: "1px",
                          color: "primary.main",
                        }}
                      />
                      <Box component="span" sx={{ minWidth: 0 }}>
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            color: "text.primary",
                            fontWeight: 600,
                          }}
                        >
                          {destination.tab === "super-investors" && (
                            <>
                              Super
                              <br />
                              Investors
                            </>
                          )}
                          {destination.tab !== "super-investors" &&
                            destination.title}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            mt: 0.75,
                            fontSize: "0.8125rem",
                            color: "text.secondary",
                            lineHeight: 1.5,
                          }}
                        >
                          {destination.description}
                        </Box>
                      </Box>
                    </Box>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default InsiderTradesLandingPage;
